import {
  ConnectionListResult,
  GridSortProps,
  PaginatedQueryWrapper,
  SelectionProviderChildProps,
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import {
  TableDownstreamOwnersFilteredByColumnQuery,
  TableDownstreamOwnersFilteredByColumnQuery_columns_downstreamOwnersConnection_nodes,
  TableDownstreamOwnersFilteredByColumnQueryVariables,
} from '../../../gen-src/queries/TableDownstreamOwnersFilteredByColumnQuery';
import {
  TableDownstreamOwnersQuery,
  TableDownstreamOwnersQuery_tables_downstreamOwnersConnection_nodes,
  TableDownstreamOwnersQueryVariables,
} from '../../../gen-src/queries/TableDownstreamOwnersQuery';
import { TableauUserSortOrder } from '../../../gen-src/queries/globalTypes';
import { TableDownstreamOwnersFilteredByColumnQueryGql } from '../../queries/TableDownstreamOwnersFilteredByColumnQueryGql';
import {
  TableDownstreamOwnersQueryGql,
} from '../../queries/TableDownstreamOwnersQueryGql';
import {
  OwnersDisplayGrid,
  OwnersDisplayGridRowData
} from '../sharedDisplayGrid/OwnersDisplayGrid';
import { OwnersValidSortFields } from '../sorting/sortProviders/OwnersGridSortProvider';

type QueryVariables = TableDownstreamOwnersQueryVariables;
type QueryResults = TableDownstreamOwnersQuery;
type QueryResultListNode = TableDownstreamOwnersQuery_tables_downstreamOwnersConnection_nodes;
type GridRowData = OwnersDisplayGridRowData;

type QueryVariablesWithFilter = TableDownstreamOwnersFilteredByColumnQueryVariables;
type QueryResultsWithFilter = TableDownstreamOwnersFilteredByColumnQuery;
type QueryResultListNodeWithFilter = TableDownstreamOwnersFilteredByColumnQuery_columns_downstreamOwnersConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.tables && resultData.tables[0] &&
    resultData.tables[0].downstreamOwnersConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.columns && resultData.columns[0] &&
    resultData.columns[0].downstreamOwnersConnection;
}

function mapResultNode(node: QueryResultListNode): GridRowData {
  return {
    // NOTE: Our selection model uses the `id`, but the "send email" API needs the users' VizPortal IDs.
    // So this ID is actually the vizportal ID. TFS 891208 will allow us to use any node property as the selection ID.
    // Using the vizportalId in the interim should work fine, however, as long as we're not querying across sites.
    id: node.vizportalId,
    name: node.name,
    username: node.username,
    domain: node.domain,
    workbookCount: node.ownedWorkbooksConnection ? node.ownedWorkbooksConnection.totalCount : null,
    datasourceCount: node.ownedDatasourcesConnection ? node.ownedDatasourcesConnection.totalCount : null,
    metricsCount: node.ownedMetricsConnection ? node.ownedMetricsConnection.totalCount : null,
    virtualConnectionsCount: node.ownedVirtualConnectionsConnection ? node.ownedVirtualConnectionsConnection.totalCount : null
  };
}

export type TableDownstreamOwnersQueryGridProps =
  SelectionProviderChildProps &
  GridSortProps<OwnersValidSortFields> & {
    readonly tableId: string;
    readonly filteredColumnId: string | null;
    readonly rowCount: number | null;
  };

export function TableDownstreamOwnersQueryGrid(props: TableDownstreamOwnersQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamOwnersLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredOwnersLineageGridMessage();

  return props.filteredColumnId ? (
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNodeWithFilter, GridRowData>
      queryGql={TableDownstreamOwnersFilteredByColumnQueryGql}
      partialQueryVariables={{ columnId: props.filteredColumnId, orderBy: props.orderBy as TableauUserSortOrder }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={mapResultNode}
    >
      {({ rowStore }) => (
        <OwnersDisplayGrid
          rowStore={rowStore}
          selectedKeys={props.selectedKeys}
          updateSelectionForKey={props.updateSelectionForKey}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      )}
    </PaginatedQueryWrapper>
  ) : (
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={TableDownstreamOwnersQueryGql}
        partialQueryVariables={{ tableId: props.tableId, orderBy: props.orderBy as TableauUserSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={mapResultNode}
      >
        {({ rowStore }) => (
          <OwnersDisplayGrid
            rowStore={rowStore}
            selectedKeys={props.selectedKeys}
            updateSelectionForKey={props.updateSelectionForKey}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    );
}
